import SuscriptionTypeFormConfig from "./form";
import GenericForm from "../GenericForm";
import { SuscriptionTypeApi, SuscriptionTypeDataType } from "@/api/SuscriptionTypeApi";
import { useEffect, useRef, useState } from "react";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { SuscriptionTypeFormData } from "@/formTypes/SuscriptionTypeDataType";
import FeatureChecklistEditor, { ChecklistValues } from "@components/forms/FeatureChecklistEditor";
import { PLAN_FEATURE_CATALOG, parsePlanFeatures } from "@/security/features.catalog";
import DocumentLimitsEditor, { DocumentLimitValues } from "@components/forms/DocumentLimitsEditor";
import { parseDocumentLimits } from "@/security/documentLimits.catalog";

type SuscriptionTypeFormProps = {
  id?: string;
  formId: string,
  saveRedirectUrl?: string,
  copyData?: boolean,
  onStored?: (suscriptionTypeData?: SuscriptionTypeDataType) => void
}

const SuscriptionTypeForm = (props: SuscriptionTypeFormProps) => {
  const { id } = props;
  const formRef = useRef<DynamicFormRef>(null);
  const [planFeatures, setPlanFeatures] = useState<ChecklistValues>({});
  const [initialFeatures, setInitialFeatures] = useState<ChecklistValues | undefined>(undefined);
  const [principalFeatures, setPrincipalFeatures] = useState<ChecklistValues>({});
  const [initialPrincipal, setInitialPrincipal] = useState<ChecklistValues | undefined>(undefined);
  const [hiddenFeatures, setHiddenFeatures] = useState<ChecklistValues>({});
  const [initialHidden, setInitialHidden] = useState<ChecklistValues | undefined>(undefined);
  const [documentLimits, setDocumentLimits] = useState<DocumentLimitValues>({});
  const [initialLimits, setInitialLimits] = useState<DocumentLimitValues | undefined>(undefined);

  useEffect(() => {
    if (id) {
      const run = async () => {
        const data = await SuscriptionTypeApi.get(id);
        const existingFeatures = parsePlanFeatures(data.features);
        // features null => plan sin restricciones: todo marcado
        setInitialFeatures(existingFeatures ?? undefined);
        setInitialPrincipal(parsePlanFeatures(data.principalFeatures) ?? undefined);
        setInitialHidden(parsePlanFeatures(data.hiddenFeatures) ?? undefined);
        setInitialLimits(parseDocumentLimits(data.documentLimits) ?? undefined);
      }
      run()
    }
  }, [id]);

  const getForm = () => {
    return SuscriptionTypeFormConfig
  }

  const saveSuscriptionTypeData = (data: SuscriptionTypeDataType): SuscriptionTypeDataType => {
    return {
      ...data,
      monto: data.monto != null ? Number(data.monto) : data.monto,
      maxUsers: data.maxUsers != null ? Number(data.maxUsers) : data.maxUsers,
      features: JSON.stringify(planFeatures),
      principalFeatures: JSON.stringify(principalFeatures),
      hiddenFeatures: JSON.stringify(hiddenFeatures),
      documentLimits: JSON.stringify(documentLimits)
    }
  }

  return (
    <>
      <GenericForm<SuscriptionTypeFormData, SuscriptionTypeDataType>
        id={props.id}
        copyData={props.copyData}
        ref={formRef}
        formId={props.formId}
        onStored={props.onStored}
        saveRedirectUrl={props.saveRedirectUrl}
        formConfig={getForm()}
        getById={(...arg) => SuscriptionTypeApi.get(...arg)}
        createFn={(data: SuscriptionTypeDataType) => SuscriptionTypeApi.create(saveSuscriptionTypeData(data))}
        updateFn={(id: string, data: SuscriptionTypeDataType) => SuscriptionTypeApi.update(id, saveSuscriptionTypeData(data))}
        successMessage="El tipo de suscripcion ha sido almacenada correctamente"
        errorMessage="No fue posible almacenar el tipo de suscripcion"
      />
      <hr />
      <h5>Funciones incluidas en el plan</h5>
      <p className="text-muted small">
        Marca una función como <b>Principal</b> para mostrarla de primero en la lista de funciones del plan.
        Marca una función como <b>Oculto</b> para que no aparezca en la comparación ni en el listado de planes
        (sigue aplicando en los permisos del usuario).
      </p>
      <FeatureChecklistEditor
        nodes={PLAN_FEATURE_CATALOG}
        initialValues={initialFeatures}
        onChange={setPlanFeatures}
        defaultValue={true}
        enablePrincipal
        initialPrincipal={initialPrincipal}
        onPrincipalChange={setPrincipalFeatures}
        enableHidden
        initialHidden={initialHidden}
        onHiddenChange={setHiddenFeatures}
      />
      <hr />
      <h5>Límites mensuales de documentos</h5>
      <p className="text-muted small">
        Define cuántos documentos de cada tipo puede emitir el tenant por mes con este plan.
        Deja el campo vacío o en <b>0</b> para no aplicar límite (ilimitado).
      </p>
      <DocumentLimitsEditor
        initialValues={initialLimits}
        onChange={setDocumentLimits}
      />
    </>
  )
}

export default SuscriptionTypeForm;
